import React from 'react';
import {
  ScheduleData,
  Selection,
  getLastDateOfPreviousSubject,
  isHoliday,
  DAY_TYPE_OPTIONS,
  LIVE_SESSION_TOPIC,
  ONLINE_WORKSHOP_TOPIC,
  ONSITE_WORKSHOP_TOPIC,
  formatDayTopicLabel,
  isSpecialScheduleDay,
} from '../utils/scheduleUtils';
import { FaPen } from 'react-icons/fa';

interface ScheduleDisplayProps {
  schedule: ScheduleData;
  selection: Selection;
  selectedDurations: { [key: string]: string };
  selectedOptions: { [key: string]: string };
  editingDateForSubject: string | null;
  customSubjectDates: { [key: string]: string };
  maxDay: number;
  isDayRestricted: (dayString: string) => boolean;
  isSubjectDateRestricted: (subjectName: string) => boolean;
  onDurationChange: (day: string, hours: string) => void;
  onDayTypeChange: (day: string, option: string) => void;
  onGoClick: (day: string, option: string, subjectName: string) => void;
  onDateEditClick: (subjectName: string) => void;
  onDateChange: (subjectName: string, newDate: string) => void;
  onDateEditCancel: () => void;
}

const ScheduleDisplay: React.FC<ScheduleDisplayProps> = ({
  schedule,
  selection,
  selectedDurations,
  selectedOptions,
  editingDateForSubject,
  customSubjectDates,
  maxDay,
  isDayRestricted,
  isSubjectDateRestricted,
  onDurationChange,
  onDayTypeChange,
  onGoClick,
  onDateEditClick,
  onDateChange,
  onDateEditCancel,
}) => {
  const options = [...DAY_TYPE_OPTIONS];

  return (
    <div className="container-fluid p-0">
      {Object.keys(schedule).length > 0 ? (
        Object.keys(schedule).map((category) => (
          <div key={category} className="mb-4 p-0">
            <div className="mb-3 p-0">
              <h5 className="text-dark fw-semibold mb-0" style={{ color: '#495057' }}>
                {category}
              </h5>
              <hr className="mt-2 mb-3 p-0" style={{ borderColor: '#e9ecef', borderWidth: '1px' }} />
            </div>

            <div className="row">
              {schedule[category].map((item, index) => {
                const topic = (item.topic || '').trim();
                // Auto-select dropdown for workshop (and other special) rows only — not LCM markers
                const rowType =
                  selectedOptions[item.day] ||
                  (topic === ONLINE_WORKSHOP_TOPIC ||
                  topic === ONSITE_WORKSHOP_TOPIC
                    ? topic
                    : '');
                return (
                <div key={index} className="col-12 mb-2 p-0">
                  <div
                    className="card border-0"
                    style={{
                      backgroundColor: '#ffffff',
                      borderRadius: '8px',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                      transition: 'box-shadow 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                    }}
                  >
                    <div className="card-body p-3">
                      <div className="row align-items-center">
                        <div className="col-md-1">
                          <span
                            className={`badge ${isDayRestricted(item.day) ? 'bg-warning text-dark' : 'bg-primary'}`}
                            style={{ fontSize: '12px', padding: '6px 10px', borderRadius: '4px' }}
                            title={
                              isDayRestricted(item.day)
                                ? `Day ${item.day.split(' ')[1]} is restricted (up to Day ${maxDay})`
                                : ''
                            }
                          >
                            {item.day}
                          </span>
                        </div>

                        <div className="col-md-4">
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <h6 className="mb-0 fw-medium text-dark" style={{ fontSize: '14px' }}>
                              {formatDayTopicLabel(item)}
                            </h6>
                            {item.live_session_lcm && !isSpecialScheduleDay(item) && (
                              <span
                                className="badge"
                                style={{
                                  fontSize: 11,
                                  backgroundColor: '#fff3cd',
                                  color: '#664d03',
                                  border: '1px solid #ffecb5',
                                }}
                                title={item.live_session_title || LIVE_SESSION_TOPIC}
                              >
                                LCM
                              </span>
                            )}
                          </div>
                        </div>

                          {/* Date */}
                          <div className="col-md-2">
                            <div className="d-flex align-items-center">
                              {editingDateForSubject === category && index === 0 ? (
                                <div className="d-flex align-items-center">
                                  <input
                                    type="date"
                                    value={item.date ? new Date(item.date).toISOString().split('T')[0] : ''}
                                    min={(() => {
                                      const subjects = Object.keys(schedule);
                                      const isFirstSubject = subjects.indexOf(category) === 0;
                                      
                                      if (isFirstSubject) {
                                        // For first subject, use the course start date
                                        return selection.startDate;
                                      } else {
                                        // For other subjects, use previous subject's last date + 1
                                        const prevLast = getLastDateOfPreviousSubject(schedule, category, selection.startDate);
                                        if (prevLast) {
                                          const d = new Date(prevLast);
                                          d.setDate(d.getDate() + 1);
                                          while (isHoliday(d, selection.saturdayStudyDay, selection.sundayHoliday)) {
                                            d.setDate(d.getDate() + 1);
                                          }
                                          return d.toISOString().split('T')[0];
                                        }
                                        return prevLast;
                                      }
                                    })()}
                                    onChange={(e) => onDateChange(category, e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Escape') {
                                        onDateEditCancel();
                                      }
                                    }}
                                    className="form-control form-control-sm"
                                    style={{ fontSize: '13px' }}
                                    autoFocus
                                  />
                                </div>
                              ) : (
                                <div className="d-flex align-items-center">
                                  <span className="text-dark" style={{ fontSize: '13px' }}>
                                    {item.date
                                      ? new Date(item.date).toLocaleDateString('en-US')
                                      : <span className="text-muted">Set date</span>}
                                  </span>
                                  {index === 0 && (
                                    <button
                                      onClick={() => onDateEditClick(category)}
                                      className="btn btn-sm btn-outline-secondary ms-2"
                                      style={{ fontSize: '10px', padding: '2px 6px' }}
                                      disabled={isSubjectDateRestricted(category)}
                                      title={isSubjectDateRestricted(category) ? `Cannot modify dates for subject "${category}" (has days within restricted range up to Day ${maxDay})` : ''}
                                    >
                                      <FaPen/>
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>

                        <div className="col-md-1">
                          <span className="text-muted" style={{ fontSize: '13px' }}>
                            {item.dayOfWeek}
                          </span>
                        </div>

                        <div className="col-md-1">
                          {item.duration ? (
                            <select
                              className="form-select form-select-sm"
                              style={{ fontSize: '13px' }}
                              onChange={(e) => onDurationChange(item.day, e.target.value)}
                              value={selectedDurations[item.day] || item.duration}
                              disabled={isDayRestricted(item.day)}
                              title={
                                isDayRestricted(item.day)
                                  ? `Cannot modify hours for Day ${item.day.split(' ')[1]} (restricted up to Day ${maxDay})`
                                  : ''
                              }
                            >
                              {Array.from({ length: 10 }, (_, i) => i + 1).map((num) => (
                                <option key={num} value={num}>
                                  {num}h
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </div>

                        <div className="col-md-1">
                          {item.duration ? (
                            <span className="text-success" style={{ fontSize: '13px' }}>
                              {item.actualTime}h
                            </span>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </div>

                        <div className="col-md-2">
                          <div className="d-flex gap-1">
                            <select
                              className="form-select form-select-sm"
                              style={{ fontSize: '12px' }}
                              onChange={(e) => onDayTypeChange(item.day, e.target.value)}
                              value={rowType}
                              disabled={isDayRestricted(item.day)}
                              title={
                                isDayRestricted(item.day)
                                  ? `Cannot add day type for Day ${item.day.split(' ')[1]} (restricted up to Day ${maxDay})`
                                  : rowType
                                    ? `Current: ${rowType}`
                                    : ''
                              }
                            >
                              <option value="">Select type</option>
                              {options.map((option) => (
                                <option key={option} value={option}>
                                  {option}
                                </option>
                              ))}
                            </select>
                            <button
                              onClick={() => {
                                onGoClick(item.day, rowType, category);
                                onDayTypeChange(item.day, '');
                              }}
                              className="btn btn-primary btn-sm"
                              style={{ fontSize: '12px', padding: '4px 8px' }}
                              disabled={isDayRestricted(item.day) || !rowType}
                              title={
                                isDayRestricted(item.day)
                                  ? `Cannot add day type for Day ${item.day.split(' ')[1]} (restricted up to Day ${maxDay})`
                                  : ''
                              }
                            >
                              Go
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        ))
      ) : (
        <div className="text-center py-4">
          <p className="text-muted mb-0">No schedule data available.</p>
        </div>
      )}
    </div>
  );
};

export default ScheduleDisplay;
